import Add from "@mui/icons-material/Add";
import Check from "@mui/icons-material/Check";
import Close from "@mui/icons-material/Close";
import Delete from "@mui/icons-material/Delete";
import Edit from "@mui/icons-material/Edit";
import DoneOutlinedIcon from "@mui/icons-material/DoneOutlined";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import FolderOutlined from "@mui/icons-material/FolderOutlined";
import FullscreenExitOutlined from "@mui/icons-material/FullscreenExitOutlined";
import FullscreenOutlined from "@mui/icons-material/FullscreenOutlined";
import HistoryOutlined from "@mui/icons-material/HistoryOutlined";
import LoopOutlined from "@mui/icons-material/LoopOutlined";
import MoreVert from "@mui/icons-material/MoreVert";
import Refresh from "@mui/icons-material/Refresh";
import SendOutlined from "@mui/icons-material/SendOutlined";
import WarningOutlined from "@mui/icons-material/WarningOutlined";
import ArrowBackOutlined from "@mui/icons-material/ArrowBackOutlined";
import AddCircleOutline from "@mui/icons-material/AddCircleOutline";
import Divider from "@mui/material/Divider";
import {
  alpha,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  IconButton,
  InputAdornment,
  Skeleton,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { Paper, List, ListItemButton, ListItemText } from "@mui/material";

import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import Save from "@mui/icons-material/Save";
import {
  ApplicationContext,
  useApplicationContext,
} from "../../../context/ApplicationContext";
import { font } from "../../../styles/font";
import { LightTooltip } from "../../styled/CustomComponent";
import {
  Suspense,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  AI_Entity,
  AI_Response_SentBy,
  Entity,
  Entity_Label,
  ModulePath,
  Severity,
} from "../../../constant/ApplicationVariables";
import {
  generateResponse_API,
  getConversationHistoryList_API,
  getConversationList_API,
  updateConversation_API,
} from "../../../api/controller/AIChatController";
import useErrorHandler from "../../../api/errorHandler/useErrorHandler";
import { HttpStatusCode } from "axios";
import { useLocation } from "react-router-dom";
import { getFolderStructureList_API } from "../../../api/controller/RequirementController";
import AutoSizer from "react-virtualized-auto-sizer";
import { Tree } from "react-arborist";
import { addModule_API } from "../../../api/controller/ModuleController";

import { debounce } from "../../../utils/Functions";
import { KeyboardShortcuts } from "../../../constant/KeyboardKeys";
import { useHotkeys } from "react-hotkeys-hook";
import FolderNode from "./saveFolder/FolderNode";
import MessagesMainContainer from "./messages";
import { lazy } from "react";
import FallbackUILoader from "../../loader/FallbackUILoader";
import { memo } from "react";

import {
  saveAICredentials_API,
  testAICredentials_API,
  getAICredentials_API,
  deleteAICredentials_API,
  isSuccess,
  getResponseData,
  getErrorMessage,
} from "../../../api/controller/artifactController";

import { fetchCompanyId } from "../../../api/controller/authController";
import { AIDisplayIcon } from "../../../components/styled/CustomIcons";

import { useModelManagement } from "../../hooks/useModelManagement";
import ModelSelectorPopover from "../../dialog/ModelSelectorPopover";
import ActionMenuPopover from "../../dialog/ActionMenuPopover";
import ConfirmDisconnectDialog from "../../dialog/ConfirmDisconnectDialog";
import ModelConfigDialog from "../../dialog/ModelConfigDialog";
import { generateChatPrompt } from "../../../utils/chatPromptGenerator";

const ChatHistoryContainer = lazy(() => import("./history"));
const FolderLocationComponent = lazy(() => import("./saveFolder"));

// ============================================================================
// SESSION STORAGE KEY for cross-module chat auto-close
// ============================================================================
const CHAT_OPENED_AT_STORAGE_KEY = "chatWindow.openedAt";

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

const handleAPIError = (error, errorHandler, setIsLoading, handleSnackbar) => {
  if (error?.response) {
    const { status, data } = error.response;

    switch (status) {
      case HttpStatusCode.BadRequest: {
        const errorMessage = data?.message || "Bad Request";
        const fieldErrors = data?.fields
          ? Object.values(data.fields).join(", ")
          : "";
        handleSnackbar(`${errorMessage} ${fieldErrors}`, Severity.error);
        break;
      }

      case HttpStatusCode.Unauthorized:
        handleSnackbar("Unauthorized - Please login again", Severity.error);
        break;

      case HttpStatusCode.Forbidden:
        handleSnackbar(
          "You don't have permission to perform this action",
          Severity.error,
        );
        break;

      case HttpStatusCode.NotFound:
        handleSnackbar("Resource not found", Severity.error);
        break;

      case HttpStatusCode.InternalServerError:
        handleSnackbar(
          "Server error occurred. Please try again later.",
          Severity.error,
        );
        break;

      case HttpStatusCode.ServiceUnavailable:
        handleSnackbar(
          "Service temporarily unavailable. Please try again later.",
          Severity.error,
        );
        break;

      default:
        handleSnackbar(
          `Error ${status}: ${data?.message || "Something went wrong"}`,
          Severity.error,
        );
    }
  } else if (error?.request) {
    handleSnackbar(
      "Network error. Please check your connection.",
      Severity.error,
    );
  } else {
    handleSnackbar(
      error?.message || "An unexpected error occurred",
      Severity.error,
    );
  }

  if (setIsLoading) {
    setIsLoading(false);
  }
};

const getEntityLabel = (entity) => {
  switch (entity) {
    case AI_Entity.userStory:
      return Entity_Label.userStory;
    case AI_Entity.useCase:
      return Entity_Label.useCase;
    case AI_Entity.testCase:
      return Entity_Label.testCase;
    case AI_Entity.folder:
      return Entity_Label.folder;
    default:
      return Entity_Label.userStory;
  }
};

const isFloxpadModel = (model) => {
  if (!model) return false;
  return (
    model.builtIn === true ||
    model.model === "FLOXPAD_LLAMA" ||
    model.model === "floxpad-ai" ||
    model.modelCode === "FLOXPAD_LLAMA" ||
    model.id === "floxpad-ai" ||
    model.id === "FLOXPAD_LLAMA" ||
    model.provider === "floxpad"
  );
};

const getBaseProvider = (providerOrModel) => {
  const baseProviders = [
    "anthropic",
    "openai",
    "gemini",
    "deepseek",
    "grok",
    "azure-openai",
    "ollama",
  ];

  if (baseProviders.includes(providerOrModel?.toLowerCase())) {
    return providerOrModel.toLowerCase();
  }

  const modelProviderMap = {
    claude: "anthropic",
    "claude-sonnet": "anthropic",
    "claude-opus": "anthropic",
    "claude-haiku": "anthropic",
    "claude-opus-4-6": "anthropic",
    "claude-sonnet-4-6": "anthropic",
    "claude-3": "anthropic",
    "claude-4": "anthropic",
    "claude-5": "anthropic",
    gpt: "openai",
    "gpt-4": "openai",
    "gpt-4o": "openai",
    "gpt-5": "openai",
    gemini: "gemini",
    "gemini-pro": "gemini",
    deepseek: "deepseek",
    "deepseek-chat": "deepseek",
    grok: "grok",
    azure: "azure-openai",
    "azure-openai": "azure-openai",
    ollama: "ollama",
  };

  const lowerInput = providerOrModel?.toLowerCase() || "";
  for (const [key, value] of Object.entries(modelProviderMap)) {
    if (lowerInput.includes(key) || key.includes(lowerInput)) {
      return value;
    }
  }

  return providerOrModel;
};

// ============================================================================
// SMART SAVE PATH RESOLVER
//
// Priority order (highest -> lowest):
//   1. Item-specific folder (chat opened from a user story / use case /
//      test case that lives inside a folder).
//   2. currentFolderData (folder the user has explicitly selected).
//   3. currentAIContextData (context picker / tree click).
//   4. Empty fallback — button shows "Select Folder".
// ============================================================================

const buildFolderFromItem = (item) => {
  if (!item) return null;

  if (item.folder && typeof item.folder === "object") {
    return {
      id: item.folder.id,
      name: item.folder.name || "",
      path: item.folder.path || "",
      description: item.folder.description || "",
      section: item.folder.section || item.section,
    };
  }

  if (typeof item.folderPath === "string" && item.folderPath.length > 0) {
    const segments = item.folderPath.split("/").filter(Boolean);
    return {
      id: item.folderId,
      name: item.folderName || segments[segments.length - 1] || "",
      path: item.folderPath,
      description: item.folderDescription || "",
      section: item.section,
    };
  }

  return null;
};

const buildFolderFromContext = (ctx) => {
  if (!ctx) return null;

  const hasPath = typeof ctx.path === "string" && ctx.path.length > 0;
  const hasName = typeof ctx.name === "string" && ctx.name.length > 0;

  if (!hasPath && !hasName) return null;

  return {
    id: ctx.id,
    name: ctx.name || (ctx.path ? ctx.path.split("/").pop() : ""),
    path: ctx.path || "",
    description: ctx.description || "",
    section: ctx.section,
  };
};

const resolveSavePath = ({
  isItemChatWindow,
  pathName,
  currentUserStoryData,
  currentUseCaseData,
  currentAIEntityData,
  currentAIContextData,
  currentFolderData,
  activeProject,
}) => {
  // 1) Item-specific folder
  if (isItemChatWindow) {
    let item = null;
    if (pathName === ModulePath.requirements) item = currentUserStoryData;
    else if (pathName === ModulePath.useCase) item = currentUseCaseData;
    else if (pathName === ModulePath.testCase) item = currentUseCaseData;
    if (!item && currentAIEntityData?.itemData)
      item = currentAIEntityData.itemData;

    const itemFolder = buildFolderFromItem(item);
    if (itemFolder && (itemFolder.path || itemFolder.name)) {
      return itemFolder;
    }
  }

  // 2) Explicitly selected folder
  const explicit = buildFolderFromContext(currentFolderData);
  if (explicit) return explicit;

  // 3) Context folder
  const fromContext = buildFolderFromContext(currentAIContextData);
  if (fromContext) return fromContext;

  // 4) Empty fallback
  return {
    id: null,
    name: "",
    path: "",
    description: "",
    section: currentAIEntityData?.entity,
    isRoot: true,
  };
};

// ============================================================================
// MAIN CHAT WINDOW COMPONENT
// ============================================================================

const ChatWindow = memo(() => {
  // ========================================================================
  // CONTEXT & HOOKS
  // ========================================================================
  const {
    toggleChatWindow,
    setToggleChatWindow,
    activeProject,
    currentFolderData,
    setCurrentFolderData,
    handleSnackbar,
    isItemChatWindow,
    setIsItemChatWindow,
    currentAIEntityData,
    setCurrentAIEntityData,
    currentUserStoryData,
    currentUseCaseData,
    getAILabel,
    setCurrentDataSavingFolder,
    setIsUpdateFolderStructure,
    currentAIContextData,
    setCurrentAIContextData,
    isAIWindowMaximized,
    setIsAIWindowMaximized,
    userDetail,
  } = useApplicationContext();

  const theme = useTheme();
  const errorHandler = useErrorHandler();
  const pathName = useLocation()?.pathname;

  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));
  const isExtraSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  useHotkeys(KeyboardShortcuts.ai.generate, () => {
    handleGenerate();
  });

  // ========================================================================
  // STATE
  // ========================================================================
  const [isLoading, setIsLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isContentGenerationLoading, setIsContentGenerationLoading] =
    useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isOpenChatHistory, setIsOpenChatHistory] = useState(false);
  const [isConversationReset, setIsConversationReset] = useState(false);
  const [isSelectingConversation, setIsSelectingConversation] = useState(false);
  const [currentCompanyId, setCurrentCompanyId] = useState(null);
  const [isLoadingCompanyId, setIsLoadingCompanyId] = useState(false);
  const [userInput, setUserInput] = useState("");
  const [responseList, setResponseList] = useState([]);
  const [showTitleActions, setShowTitleActions] = useState(false);
  const [currentConversation, setCurrentConversation] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [conversationTitle, setConversationTitle] = useState("");
  const [titleEditDialogOpen, setTitleEditDialogOpen] = useState(false);
  const [tempTitle, setTempTitle] = useState("");
  const [isLoadingConversation, setIsLoadingConversation] = useState(false);
  const [lastLoadedConversationId, setLastLoadedConversationId] = useState(
    null,
  );
  const [isInitialLoadComplete, setIsInitialLoadComplete] = useState(false);
  const [isFirstLoad, setIsFirstLoad] = useState(true);
  const [folderChangeTimeout, setFolderChangeTimeout] = useState(null);
  const [lastClearedFolder, setLastClearedFolder] = useState(null);
  const [skipFolderCheck, setSkipFolderCheck] = useState(false);
  const [modelAnchorEl, setModelAnchorEl] = useState(null);
  const [actionMenuAnchorEl, setActionMenuAnchorEl] = useState(null);
  const [actionModel, setActionModel] = useState(null);

  // ========================================================================
  // REFS
  // ========================================================================
  const wasOpenRef = useRef(false);
  const justGeneratedRef = useRef(false);
  const openedAtPathRef = useRef(null);
  const openedAtProjectRef = useRef(null);

  // Model Config Dialog State
  const [openModelSetupDialog, setOpenModelSetupDialog] = useState(false);
  const [setupProvider, setSetupProvider] = useState(null);
  const [apiKey, setApiKey] = useState("");
  const [showApiKey, setShowApiKey] = useState(false);
  const [touched, setTouched] = useState(false);
  const [status, setStatus] = useState("idle");
  const [errorMsg, setErrorMsg] = useState("");
  const [reconfiguringModel, setReconfiguringModel] = useState(null);

  const isEmpty = !apiKey?.trim();
  const showError = touched && !isEmpty;

  // Confirm Dialog State
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [confirmDialogProvider, setConfirmDialogProvider] = useState(null);

  const {
    availableModels,
    selectedModel,
    setSelectedModel,
    modelCredentials,
    setModelCredentials,
    isLoadingModels,
    isLoadingCredentials,
    failedModels,
    setFailedModels,
    isRefreshingModel,
    setIsRefreshingModel,
    fetchAvailableModels,
    fetchProviderCredentials,
    isModelConfigured,
    refreshModel,
  } = useModelManagement(currentCompanyId, userDetail, handleSnackbar);

  const conversationResponseContainerRef = useRef(null);

  // ========================================================================
  // SMART SAVE PATH
  // ========================================================================
  const resolvedSavePath = useMemo(
    () =>
      resolveSavePath({
        isItemChatWindow,
        pathName,
        currentUserStoryData,
        currentUseCaseData,
        currentAIEntityData,
        currentAIContextData,
        currentFolderData,
        activeProject,
      }),
    [
      isItemChatWindow,
      pathName,
      currentUserStoryData,
      currentUseCaseData,
      currentAIEntityData,
      currentAIContextData,
      currentFolderData,
      activeProject,
    ],
  );

  // ========================================================================
  // EFFECTS
  // ========================================================================

  // Fetch company ID
  useEffect(() => {
    const getCompanyId = async () => {
      if (userDetail?.email) {
        setIsLoadingCompanyId(true);
        try {
          const response = await fetchCompanyId();
          if (response?.status === HttpStatusCode.Ok) {
            const companyId =
              response?.data?.companyId ||
              response?.data?.id ||
              userDetail?.companyId ||
              1;
            setCurrentCompanyId(companyId);
          } else {
            setCurrentCompanyId(userDetail?.companyId || 1);
          }
        } catch (error) {
          console.error("Error fetching company ID:", error);
          setCurrentCompanyId(userDetail?.companyId || 1);
        } finally {
          setIsLoadingCompanyId(false);
        }
      }
    };

    getCompanyId();
  }, [userDetail?.email]);

  // AUTO-SYNC currentFolderData from resolvedSavePath
  useEffect(() => {
    if (!toggleChatWindow) return;
    const candidate = resolvedSavePath;
    if (!candidate) return;

    const currentPath = currentFolderData?.path ?? null;
    const candidatePath = candidate.path ?? null;
    const currentName = currentFolderData?.name ?? null;
    const candidateName = candidate.name ?? null;

    const samePath = currentPath === candidatePath;
    const sameName = currentName === candidateName;
    if (samePath && sameName) return;

    const hasSomething = candidatePath || candidateName;
    if (!hasSomething) return;

    setCurrentFolderData((prev) => ({
      ...(prev || {}),
      ...candidate,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toggleChatWindow, resolvedSavePath?.path, resolvedSavePath?.name]);

  // Snapshot on open, clear on close
  useEffect(() => {
    if (toggleChatWindow) {
      if (openedAtPathRef.current === null) {
        openedAtPathRef.current = pathName ?? null;
        openedAtProjectRef.current = activeProject?.name ?? null;

        try {
          sessionStorage.setItem(
            CHAT_OPENED_AT_STORAGE_KEY,
            JSON.stringify({
              path: pathName ?? null,
              project: activeProject?.name ?? null,
            }),
          );
        } catch (e) {
          // ignore
        }
      }
    } else {
      openedAtPathRef.current = null;
      openedAtProjectRef.current = null;

      try {
        sessionStorage.removeItem(CHAT_OPENED_AT_STORAGE_KEY);
      } catch (e) {
        // ignore
      }
    }
  }, [toggleChatWindow, pathName, activeProject?.name]);

  // Auto-close chat on module/project change
  useEffect(() => {
    if (!toggleChatWindow) return;

    let openedAtPath = openedAtPathRef.current;
    let openedAtProject = openedAtProjectRef.current;

    if (openedAtPath === null || openedAtProject === null) {
      try {
        const raw = sessionStorage.getItem(CHAT_OPENED_AT_STORAGE_KEY);
        if (raw) {
          const parsed = JSON.parse(raw);
          if (openedAtPath === null) openedAtPath = parsed?.path ?? null;
          if (openedAtProject === null)
            openedAtProject = parsed?.project ?? null;

          openedAtPathRef.current = openedAtPath;
          openedAtProjectRef.current = openedAtProject;
        }
      } catch (e) {
        // ignore
      }
    }

    if (openedAtPath === null) return;

    const currentProject = activeProject?.name ?? null;
    const pathChanged = openedAtPath !== (pathName ?? null);
    const projectChanged = openedAtProject !== currentProject;

    if (pathChanged || projectChanged) {
      console.log("[ChatWindow] Closing chat due to navigation:", {
        openedAtPath,
        currentPath: pathName,
        openedAtProject,
        currentProject,
      });

      setToggleChatWindow(false);
      if (isItemChatWindow) setIsItemChatWindow(false);
      setUserInput("");

      openedAtPathRef.current = null;
      openedAtProjectRef.current = null;

      try {
        sessionStorage.removeItem(CHAT_OPENED_AT_STORAGE_KEY);
      } catch (e) {
        // ignore
      }
    }
  }, [
    pathName,
    activeProject?.name,
    toggleChatWindow,
    isItemChatWindow,
    setToggleChatWindow,
    setIsItemChatWindow,
  ]);

  // Keep entity in sync while on the same module
  useEffect(() => {
    const path = pathName;

    if (isItemChatWindow) return;
    if (currentAIEntityData?.entity === AI_Entity.folder) return;

    let newEntity = currentAIEntityData?.entity || AI_Entity.userStory;

    if (path === ModulePath.useCase) newEntity = AI_Entity.useCase;
    else if (path === ModulePath.testCase) newEntity = AI_Entity.testCase;
    else if (path === ModulePath.requirements) newEntity = AI_Entity.userStory;

    if (
      currentAIEntityData?.entity !== newEntity &&
      !currentConversation?.conversationId
    ) {
      setCurrentAIEntityData((prev) => ({
        ...(prev || {}),
        entity: newEntity,
        label: getEntityLabel(newEntity),
      }));
    }
  }, [pathName, currentAIEntityData?.entity, isItemChatWindow]);

  const refreshModelsAfterSave = useCallback(async () => {
    await fetchAvailableModels();
    handleSnackbar("Models updated successfully", Severity.success);
  }, [fetchAvailableModels, handleSnackbar]);

  useEffect(() => {
    window.refreshModels = refreshModelsAfterSave;
    return () => {
      delete window.refreshModels;
    };
  }, [refreshModelsAfterSave]);

  useLayoutEffect(() => {
    if (toggleChatWindow && !wasOpenRef.current) {
      if (!isSmallScreen) {
        setIsAIWindowMaximized(true);
      }
    }
    wasOpenRef.current = toggleChatWindow;
  }, [toggleChatWindow, isSmallScreen]);

  useEffect(() => {
    if (toggleChatWindow && isSmallScreen) {
      setIsAIWindowMaximized(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSmallScreen]);

  // Prompt generation
  useEffect(() => {
    const newPrompt = generateChatPrompt({
      isItemChatWindow,
      currentUserStoryData,
      currentUseCaseData,
      pathName,
      currentFolderData: resolvedSavePath,
      currentAIContextData,
      currentAIEntityData,
      activeProject,
      userInput,
    });

    if (newPrompt) {
      setUserInput(newPrompt);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isItemChatWindow,
    currentUserStoryData,
    currentUseCaseData,
    pathName,
    resolvedSavePath?.path,
    currentAIContextData,
    currentAIEntityData,
    activeProject,
  ]);

  // Fetch credentials when selected model changes
  useEffect(() => {
    if (
      selectedModel?.provider &&
      selectedModel?.provider !== "unknown" &&
      selectedModel?.provider !== "floxpad" &&
      selectedModel?.id !== "floxpad-ai" &&
      !isFloxpadModel(selectedModel) &&
      currentCompanyId
    ) {
      fetchProviderCredentials(selectedModel.provider);
    }
  }, [selectedModel?.provider, currentCompanyId, fetchProviderCredentials]);

  // ========================================================================
  // FIX: FALLBACK — Listen for retryGeneration events from
  // MessagesMainContainer (in case onRetry isn't passed for some reason).
  // ========================================================================
  useEffect(() => {
    const handler = (e) => {
      const prompt = e?.detail?.prompt;
      if (!prompt) return;

      console.log("[ChatWindow] retryGeneration event received:", prompt);

      const conversationId =
        currentConversation?.conversationId ||
        currentAIEntityData?.conversationId;

      const userMessage = {
        prompt,
        chatMessageStr: prompt,
        sentBy: AI_Response_SentBy.user,
        createdAt: new Date().toISOString(),
        conversationId,
        folderPath: resolvedSavePath?.path || "",
      };

      setResponseList((prev) => [...prev, userMessage]);
      debouncedScroll();
      handleGenerateResponse(prompt, conversationId);
    };

    window.addEventListener("retryGeneration", handler);
    return () => window.removeEventListener("retryGeneration", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentConversation?.conversationId,
    currentAIEntityData?.conversationId,
    resolvedSavePath?.path,
  ]);

  // ========================================================================
  // MODEL FUNCTIONS
  // ========================================================================

  const handleSaveCredentials = async (provider, apiKey) => {
    try {
      const baseProvider = getBaseProvider(provider);
      setStatus("loading");
      setErrorMsg("");

      const companyId = currentCompanyId || userDetail?.companyId || 1;

      if (!companyId) {
        setStatus("error");
        setErrorMsg("Company ID not found. Please refresh and try again.");
        handleSnackbar("Company ID not found", Severity.error);
        return;
      }

      const response = await saveAICredentials_API({
        companyId,
        provider: baseProvider,
        apiKey,
      });

      if (isSuccess(response)) {
        setStatus("success");

        const modelId =
          reconfiguringModel?.id || reconfiguringModel?.model || baseProvider;
        setFailedModels((prev) => ({
          ...prev,
          [modelId]: false,
        }));

        setModelCredentials((prev) => ({
          ...prev,
          [baseProvider]: {
            configured: true,
            provider: baseProvider,
            apiKey,
            companyId,
          },
        }));

        await fetchAvailableModels();

        handleSnackbar(
          `${baseProvider} credentials saved successfully`,
          Severity.success,
        );

        setTimeout(() => {
          setOpenModelSetupDialog(false);
          setStatus("idle");
          setReconfiguringModel(null);
          setSetupProvider(null);
          setApiKey("");
        }, 1000);
      } else {
        setStatus("error");
        const errorMsg =
          getErrorMessage(response) ||
          `Failed to save ${baseProvider} credentials`;
        setErrorMsg(errorMsg);
        handleSnackbar(errorMsg, Severity.error);
      }
    } catch (error) {
      setStatus("error");
      const errorMsg = error.message || `Failed to save credentials`;
      setErrorMsg(errorMsg);
      handleSnackbar(errorMsg, Severity.error);
    }
  };

  const handleTestCredentials = async (provider, apiKey) => {
    try {
      const baseProvider = getBaseProvider(provider);
      const companyId = currentCompanyId || userDetail?.companyId || 1;

      if (!companyId) {
        throw new Error("Company ID not found");
      }

      const response = await testAICredentials_API({
        provider: baseProvider,
        apiKey,
        companyId,
      });

      return response;
    } catch (error) {
      console.error("Test credentials error:", error);
      throw error;
    }
  };

  const handleDeleteCredentials = async (provider) => {
    try {
      const companyId = currentCompanyId || userDetail?.companyId || 1;

      if (!companyId) {
        handleSnackbar("Company ID not found", Severity.error);
        return;
      }

      const baseProvider = getBaseProvider(provider);

      const existingCred = modelCredentials[baseProvider];
      if (existingCred && existingCred.companyId !== companyId) {
        handleSnackbar(
          `Cannot disconnect credentials for a different company`,
          Severity.error,
        );
        return;
      }

      const response = await deleteAICredentials_API(baseProvider, companyId);

      if (isSuccess(response)) {
        const modelId =
          reconfiguringModel?.id || reconfiguringModel?.model || baseProvider;
        setFailedModels((prev) => ({
          ...prev,
          [modelId]: false,
        }));

        setModelCredentials((prev) => ({
          ...prev,
          [baseProvider]: null,
        }));

        await fetchAvailableModels();

        handleSnackbar(
          `${baseProvider} disconnected successfully`,
          Severity.success,
        );

        if (
          selectedModel?.provider === baseProvider ||
          selectedModel?.id === baseProvider
        ) {
          const floxpadModel = availableModels.find((m) => isFloxpadModel(m));
          if (floxpadModel) {
            setSelectedModel(floxpadModel);
          }
        }

        handleActionMenuClose();
        setConfirmDialogOpen(false);
      } else {
        handleSnackbar(`Failed to disconnect ${baseProvider}`, Severity.error);
      }
    } catch (error) {
      handleSnackbar(`Failed to disconnect ${provider}`, Severity.error);
    }

    setModelAnchorEl(null);
  };

  const handleModelSelect = (model) => {
    setModelAnchorEl(null);

    if (!model) return;

    if (isFloxpadModel(model)) {
      setSelectedModel(model);
      if (currentConversation?.conversationId && responseList.length > 0) {
        refreshConversationWithNewModel();
      }
      return;
    }

    const isFailed = failedModels[model.id || model.model || model.modelCode];
    if (isFailed) {
      handleSnackbar(
        `This model's credentials have failed validation. Please reconfigure.`,
        Severity.warning,
      );
      handleConfigure(model);
      return;
    }

    if (model.credentialStatus === "OPEN" || model.status === "OPEN") {
      setSelectedModel(model);
      if (currentConversation?.conversationId && responseList.length > 0) {
        refreshConversationWithNewModel();
      }
      return;
    }

    setSelectedModel(model);
    handleConfigure(model);
  };

  const handleConfigure = (model) => {
    const provider =
      model?.model || model?.provider || model?.id || model?.modelCode;
    const baseProvider = getBaseProvider(provider);
    setSetupProvider(baseProvider);
    setReconfiguringModel(model);
    setApiKey("");
    setStatus("idle");
    setErrorMsg("");
    setTouched(false);
    setShowApiKey(false);
    setOpenModelSetupDialog(true);
    setModelAnchorEl(null);
  };

  const handleReconfigureModel = (model) => {
    const provider =
      model?.model || model?.provider || model?.id || model?.modelCode;
    const baseProvider = getBaseProvider(provider);
    setSetupProvider(baseProvider);
    setReconfiguringModel(model);
    setApiKey("");
    setStatus("idle");
    setErrorMsg("");
    setTouched(false);
    setShowApiKey(false);
    setOpenModelSetupDialog(true);
    setModelAnchorEl(null);
    handleActionMenuClose();
  };

  // ========================================================================
  // ACTION MENU FUNCTIONS
  // ========================================================================

  const handleActionMenuOpen = (event, model) => {
    event.stopPropagation();
    setActionMenuAnchorEl(event.currentTarget);
    setActionModel(model);
  };

  const handleActionMenuClose = () => {
    setActionMenuAnchorEl(null);
    setActionModel(null);
  };

  const handleModelAction = (action, model) => {
    if (!model) {
      console.error("No model provided to handleModelAction");
      return;
    }

    const provider =
      model?.model || model?.provider || model?.id || model?.modelCode;
    const baseProvider = getBaseProvider(provider);

    switch (action) {
      case "update":
        handleReconfigureModel(model);
        break;
      case "delete":
        setConfirmDialogProvider(baseProvider);
        setConfirmDialogOpen(true);
        break;
      default:
        break;
    }
    handleActionMenuClose();
  };

  // ========================================================================
  // CONVERSATION FUNCTIONS
  // ========================================================================

  const refreshConversationWithNewModel = async () => {
    if (currentConversation?.conversationId && responseList.length > 0) {
      setIsRefreshingModel(true);
      try {
        await handleLoadConversation(currentConversation.conversationId);
        handleSnackbar(
          "Conversation refreshed with new model",
          Severity.success,
        );
      } catch (error) {
        console.error("Error refreshing conversation:", error);
      } finally {
        setIsRefreshingModel(false);
      }
    } else {
      setModelAnchorEl(null);
    }
  };

  const handleToggleChatHistory = () => {
    const newState = !isOpenChatHistory;
    setIsOpenChatHistory(newState);

    if (newState) {
      handleLoadConversationHistory();
    }
  };

  const handleNewConversation = () => {
    try {
      const entityValue =
        currentAIEntityData?.entity ||
        (pathName === ModulePath.requirements
          ? AI_Entity.userStory
          : pathName === ModulePath.useCase
            ? AI_Entity.useCase
            : pathName === ModulePath.testCase
              ? AI_Entity.testCase
              : AI_Entity.userStory);

      const entityLabel = getEntityLabel(entityValue);

      const newConversation = {
        conversationId: null,
        title: "New Conversation",
        entity: entityValue,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        projectName: activeProject?.name,
        folderPath: resolvedSavePath?.path || "",
      };

      setCurrentConversation(newConversation);
      setResponseList([]);
      setUserInput("");
      setConversationTitle("New Conversation");
      setIsConversationReset(true);
      setShowTitleActions(false);

      setCurrentAIEntityData((prev) => ({
        ...(prev || {}),
        conversationId: null,
        entity: entityValue,
        label: entityLabel,
      }));
    } catch (error) {
      handleSnackbar("Failed to create new conversation", Severity.error);
    }
  };

  const handleSaveTitle = async () => {
    const newTitle = tempTitle.trim();

    if (!newTitle) {
      handleSnackbar("Title cannot be empty", Severity.warning);
      return;
    }

    if (newTitle.length > 50) {
      handleSnackbar("Title cannot exceed 50 characters", Severity.warning);
      return;
    }

    if (newTitle === conversationTitle) {
      setTitleEditDialogOpen(false);
      return;
    }

    try {
      setConversationTitle(newTitle);
      await handleUpdateConversationTitle(
        currentConversation?.conversationId,
        newTitle,
      );
      setTitleEditDialogOpen(false);
    } catch (error) {
      setTempTitle(conversationTitle);
    }
  };

  const handleUpdateConversationTitle = async (conversationId, newTitle) => {
    const convId = conversationId || currentConversation?.conversationId;
    let title = newTitle || conversationTitle?.trim();

    if (!convId) return;

    if (title.length < 3) {
      handleSnackbar("Title must be at least 3 characters", Severity.warning);
      return;
    }

    if (title.length > 50) {
      handleSnackbar("Title cannot exceed 50 characters", Severity.warning);
      return;
    }

    if (!title || title === "") {
      handleSnackbar("Title cannot be empty", Severity.warning);
      return;
    }

    try {
      const body = {
        conversationId: convId,
        newTitle: title,
        entity: currentAIEntityData?.entity || currentConversation?.entity,
        projectName: activeProject?.name,
        delete: false,
        folderPath: resolvedSavePath?.path || "",
      };

      const response = await updateConversation_API(body);

      if (response?.status === HttpStatusCode.Ok) {
        if (currentConversation?.conversationId === convId) {
          setCurrentConversation({
            ...currentConversation,
            title: title,
          });
          setConversationTitle(title);
        }

        setConversations(
          conversations.map((conv) =>
            conv.conversationId === convId ? { ...conv, title: title } : conv,
          ),
        );

        handleSnackbar("Conversation title updated", Severity.success);
      } else {
        errorHandler(response);
      }
    } catch (error) {
      handleAPIError(error, errorHandler, null, handleSnackbar);
    }
  };

  const handleDeleteConversation = async (conversationId) => {
    if (!conversationId) return;

    try {
      const body = {
        conversationId: conversationId,
        projectName: activeProject?.name,
        entity: currentAIEntityData?.entity,
        delete: true,
        folderPath: resolvedSavePath?.path || "",
      };

      const response = await updateConversation_API(body);

      if (response?.status === HttpStatusCode.Ok) {
        setConversations(
          conversations.filter(
            (conv) => conv.conversationId !== conversationId,
          ),
        );

        if (currentConversation?.conversationId === conversationId) {
          handleNewConversation();
        }

        handleSnackbar("Conversation deleted", Severity.success);
      } else {
        errorHandler(response);
      }
    } catch (error) {
      handleAPIError(error, errorHandler, null, handleSnackbar);
    }
  };

  const handleLoadConversation = async (conversationId) => {
    if (!conversationId) {
      handleSnackbar("No conversation ID provided", Severity.error);
      return;
    }

    if (isLoading || isSelectingConversation) return;

    if (justGeneratedRef.current) {
      console.log(
        "[ChatWindow] Skipping conversation load — generation just completed",
      );
      return;
    }

    if (lastLoadedConversationId === conversationId && responseList.length > 0) {
      console.log("Skipping reload of same conversation:", conversationId);
      return;
    }

    try {
      setIsLoading(true);
      setIsSelectingConversation(true);

      const conv = conversations.find(
        (c) => c.conversationId === conversationId,
      );
      if (!conv) {
        console.log("Conversation not found:", conversationId);
        return;
      }

      setCurrentConversation(conv);
      setConversationTitle(conv.title || "New Conversation");
      setIsConversationReset(false);
      setLastLoadedConversationId(conversationId);

      const entityValue =
        conv.entity ||
        currentAIEntityData?.entity ||
        (pathName === ModulePath.requirements
          ? AI_Entity.userStory
          : pathName === ModulePath.useCase
            ? AI_Entity.useCase
            : pathName === ModulePath.testCase
              ? AI_Entity.testCase
              : AI_Entity.userStory);

      const entityLabel = getEntityLabel(entityValue);

      setCurrentAIEntityData((prev) => ({
        ...(prev || {}),
        entity: entityValue,
        label: entityLabel,
        conversationId: conversationId,
      }));

      const conversationFolderPath =
        conv.folderPath || resolvedSavePath?.path || "";

      const body = {
        conversationId: conversationId,
        projectName: activeProject?.name,
        entity: entityValue,
        folderPath: conversationFolderPath,
      };

      const response = await getConversationList_API(body);

      if (response?.status === HttpStatusCode.Ok) {
        const messages = response.data || [];

        const formattedMessages = messages.map((msg) => ({
          ...msg,
          conversationId: conversationId,
        }));

        setResponseList(formattedMessages);

        if (isOpenChatHistory) setIsOpenChatHistory(false);

        if (messages.length > 0) {
          handleSnackbar(`Loaded: ${conv.title}`, Severity.success);
        }

        setTimeout(() => {
          handleScrollToBottom();
        }, 100);
      } else {
        errorHandler(response, setIsLoading);
      }
    } catch (error) {
      handleAPIError(error, errorHandler, setIsLoading, handleSnackbar);
    } finally {
      setTimeout(() => {
        setIsLoading(false);
        setIsSelectingConversation(false);
      }, 300);
    }
  };

  const handleLoadConversationHistory = async () => {
    if (loading) return;

    if (justGeneratedRef.current) {
      console.log(
        "[ChatWindow] Skipping history reload — generation just completed",
      );
      return;
    }

    try {
      setLoading(true);

      if (!activeProject?.name) {
        handleSnackbar("Please select a project first", Severity.warning);
        return;
      }

      const entity = currentAIEntityData?.entity || AI_Entity.userStory;
      const folderPath = resolvedSavePath?.path || "";

      const body = {
        projectName: activeProject?.name,
        entity: entity,
        folderPath: folderPath,
      };

      const response = await getConversationHistoryList_API(body);

      if (response?.status === HttpStatusCode.Ok) {
        const conversationsList = response.data || [];

        const validConversations = conversationsList.filter(
          (conv) => conv && conv.conversationId && conv.title !== null,
        );

        const sortedConversations = validConversations.sort((a, b) => {
          try {
            const dateA = new Date(
              a.lastModifiedAt || a.createdAt || a.updatedAt,
            );
            const dateB = new Date(
              b.lastModifiedAt || b.createdAt || b.updatedAt,
            );
            return dateB - dateA;
          } catch (error) {
            return 0;
          }
        });

        const conversationsWithPath = sortedConversations.map((conv) => ({
          ...conv,
          folderPath: conv.folderPath || folderPath || "",
        }));

        setConversations(conversationsWithPath);

        if (
          conversationsWithPath.length > 0 &&
          !currentConversation &&
          !isConversationReset &&
          !justGeneratedRef.current &&
          responseList.length === 0
        ) {
          const mostRecent = conversationsWithPath[0];
          await handleLoadConversation(mostRecent.conversationId);
        } else if (
          conversationsWithPath.length === 0 &&
          !justGeneratedRef.current &&
          responseList.length === 0
        ) {
          setResponseList([]);
          setCurrentConversation(null);
          setConversationTitle("New Conversation");
          setIsConversationReset(true);

          let inputValue = "";
          if (currentAIEntityData?.entity === AI_Entity.folder) {
            inputValue = `Generate ${Entity_Label.folder} for ${
              activeProject?.name
            } ${
              activeProject?.description
                ? ` - ${activeProject?.description}`
                : ""
            }`;
          } else if (
            resolvedSavePath &&
            (resolvedSavePath.path || resolvedSavePath.name)
          ) {
            const prefixText =
              pathName === ModulePath.requirements
                ? `Generate ${Entity_Label.userStory} for ${Entity_Label.folder}: `
                : pathName === ModulePath.useCase
                  ? `Generate ${Entity_Label.useCase} for ${Entity_Label.folder}: `
                  : `Generate ${Entity_Label.testCase} for ${Entity_Label.folder}: `;
            inputValue = `${prefixText} ${resolvedSavePath.name}${
              resolvedSavePath.description
                ? ` - ${resolvedSavePath.description}`
                : ""
            }`;
          }
          setUserInput(inputValue);
        }
      } else {
        errorHandler(response, setLoading);
      }
    } catch (error) {
      handleAPIError(error, errorHandler, setLoading, handleSnackbar);
    } finally {
      setTimeout(() => {
        setLoading(false);
      }, 300);
    }
  };

  // ========================================================================
  // UI FUNCTIONS
  // ========================================================================

  const handleMaximiseAIWindow = () => {
    setIsAIWindowMaximized(!isAIWindowMaximized);
  };

  const handleRefreshConversation = () => {
    if (currentConversation?.conversationId) {
      handleLoadConversation(currentConversation.conversationId);
    }
  };

  const handleClose = (e) => {
    e?.stopPropagation();
    setToggleChatWindow(false);
    setUserInput("");
    handleCloseItemChatWindow();
    setShowTitleActions(false);
  };

  const handleCloseItemChatWindow = () => {
    if (isItemChatWindow) setIsItemChatWindow(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleGenerate();
    }
  };

  const handleScrollToBottom = () => {
    const container = conversationResponseContainerRef.current;
    if (container) {
      container.scrollTo({
        top: container.scrollHeight,
        behavior: "smooth",
      });
    }
  };

  const debouncedScroll = debounce(handleScrollToBottom, 400);

  const extractMeaningfulTitle = (userInput) => {
    if (!userInput || userInput.trim() === "") {
      return "New Conversation";
    }

    const input = userInput.trim();

    if (
      input.startsWith("Generate ") ||
      input.startsWith("Create ") ||
      input.startsWith("Write ")
    ) {
      const match = input.match(
        /^(?:Generate|Create|Write)\s+(.+?)(?:\s+(?:for|about|on|regarding|:|$))/i,
      );
      if (match && match[1]) {
        let title = match[1].trim();

        title = title
          .split(/\s+/)
          .map((word) => {
            if (word === word.toUpperCase()) return word;
            return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
          })
          .join(" ");

        if (title.length > 50) {
          const words = title.split(" ");
          let shortened = "";
          for (let word of words) {
            if ((shortened + " " + word).length <= 47) {
              shortened += (shortened ? " " : "") + word;
            } else break;
          }
          title = shortened + (shortened.length < title.length ? "..." : "");
        }

        return title || "New Conversation";
      }
    }

    if (input.includes("?")) {
      const sentences = input.split(/[.!?]/);
      const firstSentence = sentences[0] + (input.includes("?") ? "?" : "");
      if (firstSentence.length > 10) {
        return firstSentence.length > 60
          ? firstSentence.substring(0, 57) + "..."
          : firstSentence;
      }
    }

    const sentences = input.split(/[.!?]/);
    if (sentences[0] && sentences[0].trim().length > 10) {
      let title = sentences[0].trim();

      title = title.replace(
        /^(?:Can you|I need|I want|Please|Could you|Would you)\s+/i,
        "",
      );

      title = title.charAt(0).toUpperCase() + title.slice(1);

      if (title.length > 60) {
        const lastSpace = title.lastIndexOf(" ", 57);
        if (lastSpace > 20) title = title.substring(0, lastSpace) + "...";
        else title = title.substring(0, 57) + "...";
      }

      return title || "New Conversation";
    }

    let cleanInput = input
      .replace(
        /^(?:Can you|I need|I want|Please|Could you|Would you|How to|What is|Tell me)\s+/i,
        "",
      )
      .trim();

    cleanInput = cleanInput.charAt(0).toUpperCase() + cleanInput.slice(1);

    if (cleanInput.length > 60) {
      cleanInput = cleanInput.substring(0, 57) + "...";
    }

    return cleanInput || "New Conversation";
  };

  // ========================================================================
  // GENERATE RESPONSE FUNCTIONS
  // ========================================================================

  const handleGenerate = async () => {
    if (!userInput || isContentGenerationLoading) return;

    if (!activeProject?.name) {
      handleSnackbar("Please select a project first", Severity.warning);
      return;
    }

    if (!isModelConfigured(selectedModel)) {
      handleSnackbar(
        `Please configure ${
          selectedModel?.label || selectedModel?.name || "model"
        } credentials first`,
        Severity.warning,
      );
      handleConfigure(selectedModel);
      return;
    }

    const conversationId =
      currentConversation?.conversationId ||
      currentAIEntityData?.conversationId;

    const userMessage = {
      prompt: userInput,
      chatMessageStr: userInput,
      sentBy: AI_Response_SentBy.user,
      createdAt: new Date().toISOString(),
      conversationId: conversationId,
      folderPath: resolvedSavePath?.path || "",
    };

    setResponseList((prev) => [...prev, userMessage]);
    const currentInput = userInput;
    setUserInput("");
    debouncedScroll();

    await handleGenerateResponse(currentInput, conversationId);
  };

  const handleGenerateResponse = async (prompt, conversationIdParam = null) => {
    try {
      setIsGenerating(true);
      setIsContentGenerationLoading(true);

      if (!activeProject?.name) {
        handleSnackbar("Please select a project first", Severity.error);
        setIsContentGenerationLoading(false);
        setIsGenerating(false);
        return;
      }

      const conversationId =
        conversationIdParam ||
        currentConversation?.conversationId ||
        currentAIEntityData?.conversationId;

      let entityValue;

      if (currentAIEntityData?.entity === AI_Entity.folder) {
        entityValue = AI_Entity.folder;
      } else if (currentConversation?.entity) {
        entityValue = currentConversation.entity;
      } else if (currentAIEntityData?.entity) {
        entityValue = currentAIEntityData.entity;
      } else {
        entityValue = AI_Entity.userStory;
      }

      let modelCode;
      if (isFloxpadModel(selectedModel)) {
        modelCode = "FLOXPAD_LLAMA";
      } else {
        modelCode =
          selectedModel?.modelCode || selectedModel?.model || selectedModel?.id;
      }

      const effectiveFolderPath = resolvedSavePath?.path || "";
      const effectiveFolderDescription =
        resolvedSavePath?.description ||
        currentFolderData?.description ||
        currentAIContextData?.description ||
        "";

      let body = {
        prompt: prompt,
        entity: entityValue,
        projectName: activeProject?.name,
        conversationId: conversationId,
        model: modelCode,
        folderPath: effectiveFolderPath,
        description: effectiveFolderDescription,
        userStoryCustomId:
          isItemChatWindow && pathName === ModulePath.requirements
            ? currentUserStoryData?.customId
            : null,
        useCaseCustomId:
          isItemChatWindow && pathName === ModulePath.useCase
            ? currentUseCaseData?.customId
            : null,
        testCaseCustomId:
          isItemChatWindow && pathName === ModulePath.testCase
            ? currentUseCaseData?.customId
            : null,
      };

      if (entityValue === AI_Entity.folder) {
        delete body.folderPath;
      }

      const response = await generateResponse_API(body);

      if (response?.status === HttpStatusCode.Ok) {
        const responseData = response.data;

        let list = [];
        if (responseData?.list && Array.isArray(responseData.list)) {
          list = responseData.list;
        } else if (
          responseData?.response?.list &&
          Array.isArray(responseData.response.list)
        ) {
          list = responseData.response.list;
        } else if (Array.isArray(responseData)) {
          list = responseData;
        } else if (
          responseData?.response &&
          typeof responseData.response === "string"
        ) {
          try {
            const parsed = JSON.parse(responseData.response);
            if (Array.isArray(parsed)) list = parsed;
            else if (parsed && typeof parsed === "object") list = [parsed];
          } catch (e) {
            console.log("Could not parse response as JSON:", e.message);
          }
        }

        if (
          list.length === 0 &&
          responseData?.chatMessage &&
          Array.isArray(responseData.chatMessage)
        ) {
          list = responseData.chatMessage;
        }

        const aiResponse = {
          ...responseData,
          sentBy: AI_Response_SentBy.assistant,
          createdAt: new Date().toISOString(),
          conversationId:
            responseData.conversationId ||
            conversationId ||
            `conv_${Date.now()}`,
          list: list,
          _rawResponse: responseData.response,
        };

        setResponseList((prev) => [...prev, aiResponse]);

        const responseConversationId = responseData.conversationId;

        const shouldUpdateTitle =
          !currentConversation ||
          !currentConversation.title ||
          currentConversation.title === "New Conversation";

        let newTitle = "New Conversation";
        if (shouldUpdateTitle) {
          newTitle = extractMeaningfulTitle(prompt);
        }

        if (!currentConversation) {
          const newConversation = {
            conversationId: responseConversationId,
            title: newTitle,
            entity: entityValue,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            projectName: activeProject?.name,
            folderPath: effectiveFolderPath,
            messages: [],
          };

          setCurrentConversation(newConversation);
          setConversationTitle(newTitle);

          if (currentAIEntityData) {
            setCurrentAIEntityData({
              ...currentAIEntityData,
              conversationId: responseConversationId,
              entity: entityValue,
              label: getEntityLabel(entityValue),
            });
          }
        } else if (shouldUpdateTitle) {
          const updatedConversation = {
            ...currentConversation,
            conversationId:
              responseConversationId || currentConversation.conversationId,
            title: newTitle,
            updatedAt: new Date().toISOString(),
          };
          setCurrentConversation(updatedConversation);
          setConversationTitle(newTitle);
        }

        if (responseConversationId) {
          try {
            const updateBody = {
              conversationId: responseConversationId,
              newTitle: shouldUpdateTitle
                ? newTitle
                : currentConversation?.title,
              entity: currentConversation?.entity || entityValue,
              projectName: activeProject?.name,
              delete: false,
              folderPath: effectiveFolderPath,
            };
            await updateConversation_API(updateBody);
          } catch (updateError) {
            console.warn("Could not update conversation title:", updateError);
          }
        }

        justGeneratedRef.current = true;

        setConversations((prev) => {
          if (!responseConversationId) return prev;

          const exists = prev.some(
            (c) => c.conversationId === responseConversationId,
          );

          const now = new Date().toISOString();

          if (exists) {
            return prev.map((c) =>
              c.conversationId === responseConversationId
                ? {
                    ...c,
                    title: shouldUpdateTitle ? newTitle : c.title,
                    updatedAt: now,
                  }
                : c,
            );
          }

          return [
            {
              conversationId: responseConversationId,
              title: shouldUpdateTitle ? newTitle : "New Conversation",
              entity: entityValue,
              createdAt: now,
              updatedAt: now,
              projectName: activeProject?.name,
              folderPath: effectiveFolderPath,
            },
            ...prev,
          ];
        });

        setTimeout(() => {
          justGeneratedRef.current = false;
        }, 2000);

        setTimeout(() => {
          handleScrollToBottom();
        }, 100);
      } else {
        const errorMsg =
          response?.data?.message ||
          response?.data?.error ||
          response?.message ||
          "";

        if (
          response?.status === HttpStatusCode.Unauthorized ||
          errorMsg.toLowerCase().includes("credential") ||
          errorMsg.toLowerCase().includes("api key") ||
          errorMsg.toLowerCase().includes("provider rejected") ||
          errorMsg.toLowerCase().includes("invalid") ||
          errorMsg.toLowerCase().includes("auth")
        ) {
          const modelId = selectedModel?.id || selectedModel?.model;
          setFailedModels((prev) => ({ ...prev, [modelId]: true }));

          handleSnackbar(
            "AI provider rejected the credentials. Please reconfigure your API key.",
            Severity.error,
          );

          if (selectedModel && !isFloxpadModel(selectedModel)) {
            handleReconfigureModel(selectedModel);
          }
        } else {
          const displayError =
            errorMsg ||
            `Failed to generate response (${
              response?.status || "unknown error"
            })`;
          handleSnackbar(displayError, Severity.error);

          const errorResponse = {
            sentBy: AI_Response_SentBy.assistant,
            createdAt: new Date().toISOString(),
            conversationId: conversationId || `conv_${Date.now()}`,
            isError: true,
            error: displayError,
            message: displayError,
            list: [],
          };
          setResponseList((prev) => [...prev, errorResponse]);
        }
      }
    } catch (error) {
      console.error("Unhandled error in handleGenerateResponse:", error);
      const errorMsg =
        error?.response?.data?.message ||
        error?.message ||
        "An unexpected error occurred";

      if (
        errorMsg.toLowerCase().includes("credential") ||
        errorMsg.toLowerCase().includes("api key") ||
        errorMsg.toLowerCase().includes("provider rejected") ||
        error?.response?.status === HttpStatusCode.Unauthorized
      ) {
        const modelId = selectedModel?.id || selectedModel?.model;
        setFailedModels((prev) => ({ ...prev, [modelId]: true }));

        handleSnackbar(
          "AI provider rejected the credentials. Please reconfigure your API key.",
          Severity.error,
        );
        if (selectedModel && !isFloxpadModel(selectedModel)) {
          handleReconfigureModel(selectedModel);
        }
      } else {
        handleSnackbar(errorMsg, Severity.error);

        const errorResponse = {
          sentBy: AI_Response_SentBy.assistant,
          createdAt: new Date().toISOString(),
          conversationId: conversationIdParam || `conv_${Date.now()}`,
          isError: true,
          error: errorMsg,
          message: errorMsg,
          list: [],
        };
        setResponseList((prev) => [...prev, errorResponse]);
      }
    } finally {
      setIsGenerating(false);
      setTimeout(() => {
        setIsContentGenerationLoading(false);
      }, 1000);
    }
  };

  // ========================================================================
  // RENDER
  // ========================================================================

  const saveButtonName =
    resolvedSavePath?.name || currentFolderData?.name || "Select Folder";

  const saveButtonData = resolvedSavePath?.path
    ? resolvedSavePath
    : currentFolderData;

  return (
    <Box
      className={`flex-1 flex relative`}
      sx={{
        position: "relative",
        containerType: "inline-size",
        height: "100%",
        minHeight: 0,
        overflow: "hidden",
        flex: 1,
        borderLeft: `1px solid ${theme.palette.divider}`,
        ...(isSmallScreen && !isAIWindowMaximized
          ? {
              width: "100%",
              maxWidth: "100%",
            }
          : {}),
      }}
    >
      <Suspense fallback={<FallbackUILoader />}>
        {isOpenChatHistory && (
          <Box
            className="flex flex-col overflow-hidden transition-transform duration-300 ease-in-out"
            sx={{
              position: "absolute",
              bgcolor: theme.palette.background.paper,
              top: 0,
              left: 0,
              width: "100%",
              maxWidth: { xs: "100%", sm: "400px" },
              height: "100%",
              zIndex: 20,
              borderRight: `1px solid ${theme.palette.divider}`,
              boxShadow: theme.shadows[4],
              "@container (min-width: 601px)": {
                position: "relative",
                left: "auto",
                top: "auto",
                zIndex: "auto",
              },
            }}
          >
            <ChatHistoryContainer
              conversations={conversations}
              currentConversationId={currentConversation?.conversationId}
              onSelectConversation={handleLoadConversation}
              onNewConversation={handleNewConversation}
              onDeleteConversation={handleDeleteConversation}
              onEditTitle={handleUpdateConversationTitle}
              handleToggleChatHistory={handleToggleChatHistory}
              loading={loading}
            />
          </Box>
        )}
      </Suspense>

      <Box
        className={`w-full h-full flex flex-col overflow-hidden`}
        sx={{ minHeight: 0 }}
      >
        {/* Header */}
        <Box
          className="flex items-center flex-nowrap gap-1 overflow-hidden"
          sx={{
            borderBottom: `1px solid ${theme.palette.divider}`,
            backgroundColor: theme.palette.background.paper,
            backdropFilter: "blur(10px)",
            minHeight: "43px",
            flexShrink: 0,
            px: isAIWindowMaximized ? 2 : 1,
          }}
        >
          <Box className="flex items-center gap-0 flex-nowrap">
            <IconButton
              size="small"
              className="flex md:hidden"
              onClick={handleToggleChatHistory}
            >
              <HistoryOutlined />
            </IconButton>

            <IconButton
              size="small"
              className="flex lg:hidden"
              onClick={() => setToggleChatWindow(false)}
            >
              <ArrowBackOutlined />
            </IconButton>

            <Box className="flex flex-1 items-center gap-2 truncate">
              <Box className="flex flex-1 flex-col justify-center">
                <Box className="flex items-center gap-2">
                  <Typography
                    variant="body2"
                    className="font-semibold truncate"
                    sx={{
                      fontWeight: 600,
                      color: theme.palette.text.primary,
                    }}
                  >
                    {currentConversation?.title || "New Conversation"}
                  </Typography>

                  <LightTooltip title="Edit title">
                    <IconButton
                      size="small"
                      onClick={() => {
                        setTempTitle(
                          currentConversation?.title || "New Conversation",
                        );
                        setConversationTitle(
                          currentConversation?.title || "New Conversation",
                        );
                        setTitleEditDialogOpen(true);
                      }}
                      sx={{
                        opacity: 0,
                        transition: "opacity 0.2s ease",
                        padding: "2px",
                        "&:hover": {
                          backgroundColor: alpha(
                            theme.palette.primary.main,
                            0.1,
                          ),
                          color: theme.palette.primary.main,
                        },
                        "@media (hover: hover)": {
                          "&:hover": {
                            opacity: 1,
                          },
                        },
                        "@media (hover: none)": {
                          opacity: 1,
                        },
                      }}
                    >
                      <Edit sx={{ fontSize: "14px" }} />
                    </IconButton>
                  </LightTooltip>
                </Box>

                {currentAIContextData && (
                  <Typography
                    variant="caption"
                    color="textSecondary"
                    className="truncate"
                    sx={{
                      fontSize: "0.7rem",
                      opacity: 0.7,
                    }}
                  >
                    Context: {currentAIContextData?.section}/
                    {currentAIContextData?.name}
                    {currentAIContextData?.description && (
                      <span style={{ opacity: 0.6, marginLeft: 4 }}>
                        - {currentAIContextData.description}
                      </span>
                    )}
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>

          <Box className="flex items-center gap-2">
            {(currentAIEntityData?.entity || currentConversation?.entity) && (
              <Chip
                label={getEntityLabel(
                  currentAIEntityData?.entity || currentConversation?.entity,
                )}
                size="small"
                sx={{
                  height: 20,
                  fontSize: "0.55rem",
                  fontWeight: 600,
                  borderRadius: "4px",
                  backgroundColor: (() => {
                    const entity =
                      currentAIEntityData?.entity ||
                      currentConversation?.entity;
                    switch (entity) {
                      case AI_Entity.userStory:
                        return alpha(theme.palette.info.main, 0.15);
                      case AI_Entity.useCase:
                        return alpha(theme.palette.success.main, 0.15);
                      case AI_Entity.testCase:
                        return alpha(theme.palette.warning.main, 0.15);
                      case AI_Entity.folder:
                        return alpha(theme.palette.primary.main, 0.6);
                      default:
                        return alpha(theme.palette.grey[400], 0.15);
                    }
                  })(),
                  color: (() => {
                    const entity =
                      currentAIEntityData?.entity ||
                      currentConversation?.entity;
                    switch (entity) {
                      case AI_Entity.userStory:
                        return theme.palette.info.main;
                      case AI_Entity.useCase:
                        return theme.palette.success.main;
                      case AI_Entity.testCase:
                        return theme.palette.warning.main;
                      case AI_Entity.folder:
                        return theme.palette.secondary.main;
                      default:
                        return theme.palette.grey[500];
                    }
                  })(),
                  "& .MuiChip-label": { px: 1 },
                }}
              />
            )}
          </Box>

          <Box className="flex items-center gap-1 ml-auto flex-nowrap">
            <LightTooltip title="Refresh messages">
              <IconButton
                size="small"
                onClick={handleRefreshConversation}
                disabled={isLoading || isRefreshingModel}
                sx={{
                  color: theme.palette.text.secondary,
                  "&:hover": {
                    color: theme.palette.primary.main,
                    backgroundColor: alpha(theme.palette.primary.main, 0.1),
                  },
                }}
              >
                {isRefreshingModel ? (
                  <CircularProgress
                    size={16}
                    sx={{ color: theme.palette.primary.main }}
                  />
                ) : (
                  <Refresh sx={{ fontSize: "18px" }} />
                )}
              </IconButton>
            </LightTooltip>

            <LightTooltip
              title={
                isAIWindowMaximized ? "Minimize window" : "Maximize window"
              }
            >
              <IconButton
                size="small"
                onClick={handleMaximiseAIWindow}
                sx={{
                  color: theme.palette.text.secondary,
                  "&:hover": {
                    color: theme.palette.primary.main,
                    backgroundColor: alpha(theme.palette.primary.main, 0.1),
                  },
                }}
              >
                {isAIWindowMaximized ? (
                  <FullscreenExitOutlined sx={{ fontSize: "18px" }} />
                ) : (
                  <FullscreenOutlined sx={{ fontSize: "18px" }} />
                )}
              </IconButton>
            </LightTooltip>

            <LightTooltip title="Close chat">
              <IconButton
                size="small"
                onClick={handleClose}
                sx={{
                  color: theme.palette.text.secondary,
                  "&:hover": {
                    color: theme.palette.error.main,
                    backgroundColor: alpha(theme.palette.error.main, 0.1),
                  },
                }}
              >
                <Close sx={{ fontSize: "20px" }} />
              </IconButton>
            </LightTooltip>
          </Box>
        </Box>

        {/* Messages Area */}
        <Box
          className="flex-1 flex flex-col items-center justify-center overflow-hidden"
          sx={{ minHeight: 0 }}
        >
          {responseList.length === 0 ? (
            <Box className="flex flex-col items-center justify-center h-full px-4 text-center">
              <Box
                sx={{
                  mb: 2,
                  p: 2,
                  borderRadius: "50%",
                  backgroundColor: alpha(theme.palette.primary.main, 0.1),
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <AIDisplayIcon
                  size="xlarge"
                  animated={true}
                  sx={{ width: 48, height: 48 }}
                />
              </Box>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Start a new conversation
              </Typography>
              <Typography
                variant="body2"
                color="textSecondary"
                sx={{ maxWidth: 400 }}
              >
                Ask me to generate user stories, use cases, test cases, or
                folders for your project.
              </Typography>
            </Box>
          ) : (
            <Box
              ref={conversationResponseContainerRef}
              className="flex-1 flex flex-col gap-3 px-4 py-4 overflow-y-auto w-full"
              sx={{ minHeight: 0 }}
            >
              <MessagesMainContainer
                responseList={responseList}
                isContentGenerationLoading={
                  isContentGenerationLoading || isRefreshingModel
                }
                onRetry={(prompt) => {
                  // ========================================================
                  // FIX: Retry handler — re-fires the SAME prompt through
                  // handleGenerateResponse so the API is called again.
                  // ========================================================
                  console.log("[ChatWindow] Retry invoked with prompt:", prompt);

                  const conversationId =
                    currentConversation?.conversationId ||
                    currentAIEntityData?.conversationId;

                  const userMessage = {
                    prompt,
                    chatMessageStr: prompt,
                    sentBy: AI_Response_SentBy.user,
                    createdAt: new Date().toISOString(),
                    conversationId,
                    folderPath: resolvedSavePath?.path || "",
                  };

                  setResponseList((prev) => [...prev, userMessage]);
                  debouncedScroll();

                  handleGenerateResponse(prompt, conversationId);
                }}
              />
            </Box>
          )}
        </Box>

        {/* Input Area */}
        <Box className="flex flex-col items-center w-full border-t border-divider">
          <Container
            maxWidth="md"
            className={`px-4 py-3 flex flex-col gap-2 w-full`}
            sx={{ px: { xs: 2, sm: 4 } }}
          >
            <Box className="flex items-center justify-between w-full">
              <Suspense fallback={<FallbackUILoader />}>
                <Box className="min-w-fit max-w-fit">
                  <FolderLocationComponent
                    disabled={responseList?.length === 0}
                    currentFolderName={saveButtonName}
                    currentFolderData={saveButtonData}
                    entity={
                      currentAIEntityData?.entity ||
                      currentConversation?.entity
                    }
                    onFolderChange={(newFolder) => {
                      if (newFolder) {
                        setCurrentFolderData({
                          id: newFolder.id,
                          name: newFolder.name,
                          path: newFolder.path,
                          description: newFolder.description,
                          section: newFolder.section,
                        });
                        setCurrentAIContextData({
                          id: newFolder.id,
                          name: newFolder.name,
                          path: newFolder.path,
                          description: newFolder.description,
                          section: newFolder.section,
                        });
                      }
                    }}
                  />
                </Box>
              </Suspense>
            </Box>

            <Box className="flex flex-col gap-2 w-full">
              <Box sx={{ position: "relative" }}>
                <TextField
                  size="small"
                  placeholder={`Ask ${getAILabel()
                    ?.replace("AI assistance for:", "")
                    .trim()} assistant...`}
                  disabled={
                    isContentGenerationLoading ||
                    isLoadingModels ||
                    isRefreshingModel ||
                    isLoadingCompanyId
                  }
                  autoFocus
                  fullWidth
                  multiline
                  minRows={3}
                  maxRows={4}
                  value={userInput}
                  onChange={(e) => setUserInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: theme.shape.borderRadius,
                      border: `1px solid ${theme.palette.divider}`,
                      paddingRight: "50px",
                      paddingTop: "8px",
                      paddingBottom: "48px",
                      transition: "all 0.2s ease-in-out",
                      backgroundColor: theme.palette.background.paper,
                      "&:hover": {
                        borderColor: theme.palette.primary.main,
                        boxShadow: `0 2px 8px ${alpha(
                          theme.palette.primary.main,
                          0.1,
                        )}`,
                      },
                      "&.Mui-focused": {
                        borderColor: theme.palette.primary.main,
                        borderWidth: "2px",
                        boxShadow: `0 4px 12px ${alpha(
                          theme.palette.primary.main,
                          0.15,
                        )}`,
                      },
                      "& .MuiInputBase-input": {
                        fontSize: font.size.fs_5,
                        lineHeight: "1.5",
                        "&::placeholder": {
                          color: theme.palette.text.disabled,
                          opacity: 0.8,
                          fontStyle: "italic",
                        },
                      },
                    },
                    "& .MuiOutlinedInput-input": {
                      paddingRight: "0 !important",
                      paddingTop: "10px !important",
                      paddingBottom: "10px !important",
                    },
                  }}
                />

                {/* Model Selector Button */}
                <Box
                  sx={{
                    position: "absolute",
                    left: "12px",
                    bottom: "12px",
                    zIndex: 1,
                  }}
                >
                  <Button
                    size="small"
                    onClick={(e) => setModelAnchorEl(e.currentTarget)}
                    disabled={
                      isLoadingModels ||
                      isRefreshingModel ||
                      isLoadingCompanyId
                    }
                    sx={{
                      textTransform: "none",
                      borderRadius: "16px",
                      color: theme.palette.text.secondary,
                      minWidth: "auto",
                      padding: "4px 10px",
                      fontSize: "0.75rem",
                      backgroundColor: alpha(
                        theme.palette.background.paper,
                        0.8,
                      ),
                      border: `1px solid ${theme.palette.divider}`,
                      "&:hover": {
                        backgroundColor: alpha(
                          theme.palette.primary.main,
                          0.08,
                        ),
                        borderColor: theme.palette.primary.main,
                      },
                      "& .MuiButton-endIcon": {
                        marginLeft: "4px",
                      },
                    }}
                    endIcon={<KeyboardArrowDownIcon sx={{ fontSize: 16 }} />}
                  >
                    {isLoadingModels || isLoadingCompanyId ? (
                      <CircularProgress size={14} />
                    ) : isRefreshingModel ? (
                      <CircularProgress
                        size={14}
                        sx={{ color: theme.palette.primary.main }}
                      />
                    ) : (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                        }}
                      >
                        {isModelConfigured(selectedModel) ? (
                          <CheckCircleOutlineIcon
                            sx={{
                              fontSize: 14,
                              color: theme.palette.success.main,
                            }}
                          />
                        ) : (
                          <LockOutlinedIcon
                            sx={{
                              fontSize: 14,
                              color: theme.palette.error.main,
                            }}
                          />
                        )}
                        <Typography variant="caption" sx={{ fontWeight: 500 }}>
                          {selectedModel?.label ||
                            selectedModel?.name ||
                            selectedModel?.model ||
                            "Select Model"}
                        </Typography>
                      </Box>
                    )}
                  </Button>
                </Box>

                {/* Model Selector Popover */}
                <ModelSelectorPopover
                  anchorEl={modelAnchorEl}
                  onClose={() => setModelAnchorEl(null)}
                  availableModels={availableModels}
                  isLoadingModels={isLoadingModels}
                  selectedModel={selectedModel}
                  onModelSelect={handleModelSelect}
                  onConfigure={handleConfigure}
                  onReconfigure={handleReconfigureModel}
                  onRefresh={() => {
                    refreshModel();
                    handleSnackbar("Models refreshed", Severity.success);
                  }}
                  onAddModel={() => {
                    setModelAnchorEl(null);
                    setSetupProvider("generic");
                    setReconfiguringModel(null);
                    setOpenModelSetupDialog(true);
                    setApiKey("");
                    setStatus("idle");
                    setErrorMsg("");
                    setTouched(false);
                    setShowApiKey(false);
                  }}
                  onActionMenuOpen={handleActionMenuOpen}
                  isFloxpadModel={isFloxpadModel}
                  failedModels={failedModels}
                  currentCompanyId={currentCompanyId}
                  userDetail={userDetail}
                />
                {/* Action Menu Popover */}
                <ActionMenuPopover
                  anchorEl={actionMenuAnchorEl}
                  onClose={handleActionMenuClose}
                  actionModel={actionModel}
                  onUpdate={handleModelAction}
                  onDelete={handleModelAction}
                />

                {/* Confirm Disconnect Dialog */}
                <ConfirmDisconnectDialog
                  open={confirmDialogOpen}
                  onClose={() => setConfirmDialogOpen(false)}
                  provider={confirmDialogProvider}
                  onConfirm={() => {
                    if (confirmDialogProvider) {
                      handleDeleteCredentials(confirmDialogProvider);
                    }
                  }}
                />

                {/* Send Button */}
                <Box
                  sx={{
                    position: "absolute",
                    right: "12px",
                    bottom: "12px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    zIndex: 1,
                  }}
                >
                  <Tooltip
                    title={
                      isModelConfigured(selectedModel)
                        ? "Send message (Enter)"
                        : "Configure credentials to use this model"
                    }
                    placement="top"
                  >
                    <span>
                      <IconButton
                        size="small"
                        disabled={
                          isContentGenerationLoading ||
                          !userInput.trim() ||
                          isLoadingModels ||
                          isRefreshingModel ||
                          isLoadingCompanyId ||
                          !isModelConfigured(selectedModel)
                        }
                        onClick={handleGenerate}
                        sx={{
                          backgroundColor: theme.palette.primary.main,
                          color: theme.palette.primary.contrastText,
                          width: "32px",
                          height: "32px",
                          transition: "all 0.2s ease-in-out",
                          "&:hover": {
                            backgroundColor: theme.palette.primary.dark,
                            transform: "translateY(-2px) scale(1.05)",
                            boxShadow: theme.shadows[4],
                          },
                          "&:active": {
                            transform: "translateY(0) scale(0.95)",
                          },
                          "&.Mui-disabled": {
                            backgroundColor:
                              theme.palette.action.disabledBackground,
                            color: theme.palette.action.disabled,
                          },
                          "& .MuiSvgIcon-root": {
                            fontSize: "1rem",
                          },
                        }}
                      >
                        <SendOutlined fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Box>

                {userInput.length > 0 && (
                  <Typography
                    variant="caption"
                    sx={{
                      position: "absolute",
                      right: "12px",
                      bottom: "50px",
                      color:
                        userInput.length > 1000
                          ? theme.palette.warning.main
                          : theme.palette.text.disabled,
                      fontSize: "0.7rem",
                      backgroundColor: alpha(
                        theme.palette.background.paper,
                        0.8,
                      ),
                      padding: "2px 6px",
                      borderRadius: "12px",
                      pointerEvents: "none",
                    }}
                  >
                    {userInput.length}/2000
                  </Typography>
                )}
              </Box>

              <Box className="flex items-center justify-between px-1">
                <Typography
                  variant="caption"
                  sx={{
                    color: theme.palette.text.disabled,
                    fontSize: "0.65rem",
                  }}
                >
                  Press Enter to send, Shift+Enter for new line
                </Typography>

                {(isContentGenerationLoading || isRefreshingModel) && (
                  <Box className="flex items-center gap-1">
                    <CircularProgress size={12} thickness={6} />
                    <Typography
                      variant="caption"
                      sx={{
                        color: theme.palette.primary.main,
                        fontSize: "0.65rem",
                      }}
                    >
                      {isRefreshingModel ? "Refreshing..." : "Generating..."}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          </Container>
        </Box>
      </Box>

      {/* Dialogs */}
      <Dialog
        open={titleEditDialogOpen}
        onClose={() => {
          setTitleEditDialogOpen(false);
          setTempTitle(conversationTitle);
        }}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: theme.shape.borderRadius,
            boxShadow: theme.shadows[8],
            backgroundColor: theme.palette.background.paper,
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 600 }}>
          Edit Conversation Title
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Title"
            type="text"
            fullWidth
            value={tempTitle}
            onChange={(e) => setTempTitle(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleSaveTitle();
              if (e.key === "Escape") {
                setTitleEditDialogOpen(false);
                setTempTitle(conversationTitle);
              }
            }}
            inputProps={{ maxLength: 50 }}
            helperText={`${tempTitle?.length || 0}/50 characters`}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 0 }}>
          <Button
            onClick={() => {
              setTitleEditDialogOpen(false);
              setTempTitle(conversationTitle);
            }}
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSaveTitle}
            variant="contained"
            disabled={
              !tempTitle.trim() || tempTitle.trim() === conversationTitle
            }
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <ModelConfigDialog
        open={openModelSetupDialog}
        onClose={() => {
          setOpenModelSetupDialog(false);
          setStatus("idle");
          setErrorMsg("");
          setReconfiguringModel(null);
          setSetupProvider(null);
          setApiKey("");
          setTouched(false);
        }}
        provider={setupProvider}
        apiKey={apiKey}
        setApiKey={setApiKey}
        status={status}
        setStatus={setStatus}
        errorMsg={errorMsg}
        setErrorMsg={setErrorMsg}
        touched={touched}
        setTouched={setTouched}
        showApiKey={showApiKey}
        setShowApiKey={setShowApiKey}
        handleTestCredentials={handleTestCredentials}
        handleSaveCredentials={handleSaveCredentials}
        reconfiguringModel={reconfiguringModel}
        isLoading={isLoadingModels || isLoadingCredentials}
        onSaveSuccess={() => {
          fetchAvailableModels();
          handleSnackbar(
            "Credentials saved and models updated",
            Severity.success,
          );
        }}
      />
    </Box>
  );
});

export default ChatWindow;

// ============================================================================
// ResponseSaveComponent (unchanged)
// ============================================================================
function ResponseSaveComponent({
  responseSaveAnchorEl,
  handleCloseSavePopup,
  handleContinueSavePopup,
  entity,
  foldersList,
  setFoldersList,
}) {
  const {
    folderStructureList,
    setFolderStructureList,
    activeProject,
    handleSnackbar,
    currentAIEntityData,
    currentDataSavingFolder,
    setCurrentDataSavingFolder,
  } = useApplicationContext();
  const theme = useTheme();
  const pathName = useLocation()?.pathname;
  const errorHandler = useErrorHandler();

  const [loading, setLoading] = useState(false);
  const [openCreateFolder, setOpenCreateFolder] = useState(false);

  const [folderText, setFolderText] = useState("");
  const [selectedFolder, setSelectedFolder] = useState(null);

  const handleCloseFolderCreate = () => {
    setOpenCreateFolder(false);
    setFolderText("");
  };

  const handleContinue = () => {
    handleContinueSavePopup(selectedFolder);
  };

  const handleCreateFolder = async () => {
    try {
      setLoading(true);

      let body = {
        name: folderText,
        projectName: activeProject?.name,
        section:
          entity === AI_Entity.userStory
            ? Entity.userStory
            : entity === AI_Entity.useCase
              ? Entity.useCase
              : Entity.testCase,
        path: selectedFolder ? selectedFolder?.data?.path : "",
      };

      const response = await addModule_API(body);

      if (response) {
        if (response.status === HttpStatusCode.Ok) {
          handleCloseFolderCreate();
          handleSnackbar("Folder created successfully", Severity.success);
        } else {
          errorHandler(response, setLoading);
        }
      }
    } catch (error) {
      handleAPIError(error, errorHandler, setLoading, handleSnackbar);
    }
  };

  return (
    <Dialog
      open={responseSaveAnchorEl}
      onClose={handleCloseSavePopup}
      sx={{
        "& .MuiDialog-paper": {
          width: "100%",
          maxWidth: "400px",
          height: "60vh",
        },
      }}
    >
      <Box className="flex flex-col gap-0 h-full w-full">
        <Box className="flex flex-col gap-2 px-4 py-2 border-b">
          <Typography fontSize={font.size.fs_4}>Folder Location</Typography>
        </Box>

        <Box
          className="flex-1 flex flex-col gap-2 relative"
          onContextMenu={(e) => e.preventDefault()}
        >
          <Box className="flex h-full w-full">
            <Box className="flex h-full px-4 py-2 w-full">
              {loading ? (
                <Box className="flex flex-col gap-2 h-full w-full overflow-y-auto">
                  {Array.from({ length: 9 }, (_, index) => (
                    <Skeleton key={index} variant="rounded" height={30} />
                  ))}
                </Box>
              ) : (
                <Box className="flex-1 flex flex-col gap-2">
                  <AutoSizer>
                    {({ height, width }) => (
                      <Tree
                        initialData={foldersList}
                        openByDefault={false}
                        width={width}
                        height={height}
                        indent={24}
                        rowHeight={38}
                      >
                        {({ ...props }) => (
                          <FolderNode
                            {...props}
                            selectedFolder={selectedFolder}
                            setSelectedFolder={setSelectedFolder}
                          />
                        )}
                      </Tree>
                    )}
                  </AutoSizer>
                </Box>
              )}
            </Box>
          </Box>
        </Box>

        <Box className="flex items-center justify-end gap-2 px-4 py-2 border-t">
          <Button
            color="primary"
            variant="outlined"
            disabled={loading}
            onClick={() => setOpenCreateFolder(!openCreateFolder)}
          >
            Add Folder
          </Button>
          {openCreateFolder && (
            <TextField
              placeholder="Folder Name"
              value={folderText}
              onChange={(e) => setFolderText(e.target.value)}
              InputProps={{
                endAdornment: (
                  <Box className="flex items-center gap-0">
                    <IconButton onClick={() => handleCreateFolder()}>
                      <Check />
                    </IconButton>
                    <IconButton onClick={() => handleCloseFolderCreate()}>
                      <Close />
                    </IconButton>
                  </Box>
                ),
              }}
            />
          )}

          <Button
            color="inherit"
            variant="text"
            sx={{ ml: "auto" }}
            onClick={handleCloseSavePopup}
          >
            Cancel
          </Button>
          <Button
            color="primary"
            variant="contained"
            disabled={loading || !selectedFolder}
            onClick={handleContinue}
          >
            Select
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
}

// ============================================================================
// ChooseContextComponent (unchanged)
// ============================================================================
function ChooseContextComponent() {
  const {
    currentDataSavingFolder,
    setCurrentDataSavingFolder,
    currentAIEntityData,
    setCurrentAIEntityData,
    activeProject,
    isItemChatWindow,
    handleSnackbar,
    currentAIResponseSaveData,
    setCurrentAIResponseSaveData,
    currentFolderData,
    setCurrentFolderData,
    setUseCaseList,
    setUserStoryList,
    setIsUpdateFolderStructure,
    currentAIContextData,
    setCurrentAIContextData,
  } = useApplicationContext();
  const theme = useTheme();
  const pathName = useLocation()?.pathname;
  const errorHandler = useErrorHandler();

  const [foldersList, setFoldersList] = useState([]);
  const [responseSaveAnchorEl, setResponseSaveAnchorEl] = useState(false);
  const [isDataSaveLoading, setIsDataSaveLoading] = useState(false);
  const [isDuplicateSaveAllowed, setIsDuplicateSaveAllowed] = useState(false);
  const [isDuplicateSaveResponse, setIsDuplicateSaveResponse] = useState(false);
  const [currentTabValue, setCurrentTabValue] = useState(AI_Entity.userStory);
  const [loading, setLoading] = useState(false);
  const [openCreateFolder, setOpenCreateFolder] = useState(false);
  const [folderText, setFolderText] = useState("");
  const [selectedFolder, setSelectedFolder] = useState(null);

  const handleChangeTab = (event, newValue) => {
    setCurrentTabValue(newValue);
    setCurrentAIEntityData((prev) => ({
      ...(prev || {}),
      entity: newValue,
      label: getEntityLabel(newValue),
    }));
  };

  const handleOpenFolderPopup = (e) => {
    setResponseSaveAnchorEl(true);
  };

  const handleCloseFolderPopup = () => {
    setResponseSaveAnchorEl(false);
  };

  const handleContinueSavePopup = (folder) => {
    setCurrentAIContextData(folder?.data);
    handleCloseFolderPopup();
  };

  const handleCloseFolderCreate = () => {
    setOpenCreateFolder(false);
    setFolderText("");
  };

  const handleContinue = () => {
    handleContinueSavePopup(selectedFolder);
  };

  const handleCreateFolder = async () => {
    try {
      setLoading(true);

      let body = {
        name: folderText,
        projectName: activeProject?.name,
        section:
          currentAIEntityData?.entity === AI_Entity.userStory
            ? Entity.userStory
            : currentAIEntityData?.entity === AI_Entity.useCase
              ? Entity.useCase
              : Entity.testCase,
        path: selectedFolder ? selectedFolder?.data?.path : "",
      };

      const response = await addModule_API(body);

      if (response?.status === HttpStatusCode.Ok) {
        handleGetFoldersList(
          activeProject,
          currentAIEntityData?.entity || currentTabValue,
        );
        handleCloseFolderCreate();
        handleSnackbar("Folder created successfully", Severity.success);
      } else {
        errorHandler(response, setLoading);
      }
    } catch (error) {
      handleAPIError(error, errorHandler, setLoading, handleSnackbar);
    }
  };

  const handleGetFoldersList = async (projectData, entity) => {
    try {
      setLoading(true);

      let projectName = projectData?.name;

      let body = {
        projectName: projectName,
        section:
          entity === AI_Entity.userStory
            ? AI_Entity.userStory
            : entity === AI_Entity.useCase
              ? AI_Entity.useCase
              : AI_Entity.testCase,
      };

      const response = await getFolderStructureList_API(body);

      if (response?.status === HttpStatusCode.Ok) {
        setFoldersList(response.data);
      } else {
        errorHandler(response, setLoading);
      }
    } catch (error) {
      handleAPIError(error, errorHandler, setLoading, handleSnackbar);
    }
  };

  useEffect(() => {
    if (activeProject && responseSaveAnchorEl) {
      const entity = currentAIEntityData?.entity || AI_Entity.userStory;
      setCurrentTabValue(entity);
      handleGetFoldersList(activeProject, entity);
    }
  }, [activeProject, responseSaveAnchorEl, currentAIEntityData?.entity]);

  return (
    <Box
      className="flex-1 max-w-fit flex items-center gap-2 overflow-x-auto"
      borderRadius={theme.shape.borderRadius}
    >
      <Box className="flex items-center gap-2">
        {currentAIContextData ? (
          <LightTooltip
            title={`Context: ${currentAIContextData?.section}/${currentAIContextData?.path}`}
          >
            <Box
              className={`flex items-center px-2 py-1 gap-1 w-fit overflow-hidden`}
              sx={{
                border: `1px solid ${theme.palette.primary.main}`,
                backgroundColor: alpha(theme.palette.primary.main, 0.1),
                color: theme.palette.primary.main,
                fontSize: font.size.fs_5,
                borderRadius: theme.shape.borderRadius,
                cursor: "pointer",
                transition: "all 0.2s ease",
                "&:hover": {
                  backgroundColor: alpha(theme.palette.primary.main, 0.15),
                  borderColor: theme.palette.primary.dark,
                  transform: "translateY(-1px)",
                  boxShadow: theme.shadows[1],
                },
              }}
              onClick={handleOpenFolderPopup}
            >
              <FolderOutlined
                sx={{ color: "inherit", fontSize: font.size.fs_5 }}
              />

              <Typography
                fontSize={font.size.fs_5}
                color={"inherit"}
                className="flex-1 truncate"
                sx={{ fontWeight: 500 }}
              >
                {currentAIContextData?.section}/{currentAIContextData?.name}
              </Typography>

              <LightTooltip title={`Change Context`}>
                <IconButton
                  onClick={handleOpenFolderPopup}
                  sx={{
                    padding: "2px",
                    color: "inherit",
                    "&:hover": {
                      backgroundColor: alpha(theme.palette.primary.main, 0.2),
                    },
                  }}
                >
                  <LoopOutlined sx={{ fontSize: font.size.fs_5 }} />
                </IconButton>
              </LightTooltip>
              <LightTooltip title={`Close Context`}>
                <IconButton
                  onClick={(e) => {
                    e.stopPropagation();
                    setCurrentAIContextData(null);
                  }}
                  sx={{
                    padding: "2px",
                    color: "inherit",
                    "&:hover": {
                      backgroundColor: alpha(theme.palette.error.main, 0.1),
                      color: theme.palette.error.main,
                    },
                  }}
                >
                  <Close sx={{ fontSize: font.size.fs_5 }} />
                </IconButton>
              </LightTooltip>
            </Box>
          </LightTooltip>
        ) : (
          <LightTooltip title={`Select Context`}>
            <IconButton
              onClick={handleOpenFolderPopup}
              sx={{
                color: theme.palette.text.secondary,
                "&:hover": {
                  backgroundColor: alpha(theme.palette.primary.main, 0.1),
                  color: theme.palette.primary.main,
                },
              }}
            >
              <AddCircleOutline />
            </IconButton>
          </LightTooltip>
        )}
      </Box>

      {responseSaveAnchorEl && (
        <Dialog
          open={responseSaveAnchorEl}
          onClose={handleCloseFolderPopup}
          sx={{
            "& .MuiDialog-paper": {
              width: "100%",
              maxWidth: "400px",
              height: "60vh",
              borderRadius: theme.shape.borderRadius,
            },
          }}
          onBackdropClick={handleCloseFolderPopup}
          onEscapeKeyDown={handleCloseFolderPopup}
          onSubmit={handleContinue}
        >
          <Box className="flex flex-col gap-0 h-full w-full">
            <Box className="flex flex-col gap-2 px-6 py-4 border-b">
              <Typography
                variant="h6"
                sx={{ fontWeight: 600, color: theme.palette.text.primary }}
              >
                Select Context
              </Typography>
            </Box>

            <Box className="flex gap-2 px-6 py-3 border-b">
              <Tabs
                variant="scrollable"
                value={currentTabValue || AI_Entity.userStory}
                onChange={handleChangeTab}
                sx={{
                  minHeight: "40px",
                  "& .MuiTab-root": {
                    minHeight: "40px",
                    fontSize: font.size.fs_5,
                    fontWeight: 500,
                    textTransform: "none",
                  },
                }}
              >
                {[
                  {
                    label: Entity_Label.userStory,
                    value: AI_Entity.userStory,
                  },
                  { label: Entity_Label.useCase, value: AI_Entity.useCase },
                  { label: Entity_Label.testCase, value: AI_Entity.testCase },
                ]?.map((item, index) => (
                  <Tab key={index} label={item.label} value={item?.value} />
                ))}
              </Tabs>
            </Box>

            <Box
              className="flex-1 flex flex-col gap-2 relative"
              onContextMenu={(e) => e.preventDefault()}
            >
              <Box className="flex h-full w-full">
                <Box className="flex-1 flex h-full px-4 py-4 w-full">
                  {loading ? (
                    <Box className="flex-1 flex flex-col gap-2 overflow-y-auto">
                      {Array.from({ length: 8 }, (_, index) => (
                        <Skeleton key={index} variant="rounded" height={30} />
                      ))}
                    </Box>
                  ) : (
                    <AutoSizer>
                      {({ height, width }) => (
                        <Tree
                          initialData={foldersList}
                          openByDefault={false}
                          width={width}
                          height={height}
                          indent={24}
                          rowHeight={38}
                        >
                          {({ ...props }) => (
                            <FolderNode
                              {...props}
                              selectedFolder={selectedFolder}
                              setSelectedFolder={setSelectedFolder}
                              handleGetSaveLocationDetails={() => {}}
                            />
                          )}
                        </Tree>
                      )}
                    </AutoSizer>
                  )}
                </Box>
              </Box>
            </Box>

            <Box className="flex items-center justify-end gap-2 px-6 py-4 border-t">
              <Button
                color="inherit"
                variant="text"
                onClick={handleCloseFolderPopup}
                sx={{
                  color: theme.palette.text.secondary,
                  "&:hover": {
                    backgroundColor: alpha(theme.palette.divider, 0.2),
                  },
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                color="primary"
                disabled={loading || !selectedFolder}
                onClick={handleContinue}
                sx={{
                  color: theme.palette.primary.contrastText,
                  fontWeight: 600,
                  "&:hover": {
                    backgroundColor: theme.palette.primary.dark,
                    transform: "translateY(-2px)",
                    boxShadow: theme.shadows[2],
                  },
                  "&.Mui-disabled": {
                    backgroundColor: theme.palette.action.disabledBackground,
                    color: theme.palette.action.disabled,
                  },
                }}
              >
                Select
              </Button>
            </Box>
          </Box>
        </Dialog>
      )}
    </Box>
  );
}